import { useState } from "react";
import { useParams } from "react-router-dom";
import { useWorkspace } from "../api/hooks/useWorkspace";
import { useRepos } from "../repos/useRepos";
import { useTokensList, useCreateToken, useRevokeToken } from "./useTokens";
import { TokensTable } from "./TokensTable";
import { NewTokenModal } from "./NewTokenModal";
import { RevokeConfirmDialog } from "./RevokeConfirmDialog";
import { EmptyState, Tooltip } from "@archlang/ui";
import { Banner } from "../ui/Banner";
import { Button } from "../ui/Button";
import { SkeletonRows } from "../ui/Skeleton";
import { useToast } from "../ui/Toast";
import { PageHeader } from "../ui/PageHeader";
import { useTranslation } from "../i18n";
import type { TokenRow, CreateTokenInput } from "./types";

type FilterMode = "this" | "all";

export function TokensPage() {
  const { t } = useTranslation("tokens");
  const { slug = "" } = useParams<{ slug: string }>();
  const toast = useToast();

  const wsQuery = useWorkspace(slug);
  const workspace = wsQuery.data;
  const currentWorkspaceId = workspace?.id ?? null;
  // Repos the new-token modal can narrow a token to (and build an MCP URL from).
  const reposQuery = useRepos(slug);

  const tokensQuery = useTokensList(undefined); // always fetch all, filter client-side
  const createToken = useCreateToken(undefined);
  const revokeToken = useRevokeToken(undefined);

  const [filterMode, setFilterMode] = useState<FilterMode>("this");
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<TokenRow | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  // Filter tokens
  const allTokens = (tokensQuery.data ?? []).filter(
    (tok) => tok.revokedAt === null,
  );
  const filteredTokens =
    filterMode === "this"
      ? allTokens.filter(
          (tok) => tok.workspaceId === currentWorkspaceId || tok.workspaceId === null,
        )
      : allTokens;

  async function handleCreate(input: CreateTokenInput) {
    return createToken.mutateAsync(input);
  }

  async function handleRevoke(token: TokenRow) {
    setRevokeError(null);
    try {
      await revokeToken.mutateAsync({ id: token.id });
      setRevoking(null);
      toast.success(t("toast.revoked", { name: token.name }));
    } catch (err) {
      const message = err instanceof Error ? err.message : t("error.generic");
      setRevokeError(message);
    }
  }

  return (
    <div>
      <PageHeader
        title={t("page.title")}
        description={t("page.description")}
        actions={
          <Button variant="primary" onClick={() => setCreating(true)}>
            {t("page.newToken")}
          </Button>
        }
      />

      {/* Error */}
      {tokensQuery.isError && (
        <Banner kind="error" className="mb-4">
          {tokensQuery.error instanceof Error
            ? tokensQuery.error.message
            : t("page.loadFailed")}
          <Button
            variant="ghost"
            size="sm"
            className="ml-3"
            onClick={() => tokensQuery.refetch()}
          >
            {t("page.retry")}
          </Button>
        </Banner>
      )}

      {/* Loading */}
      {tokensQuery.isLoading && (
        <>
          <span className="sr-only">{t("page.loading")}</span>
          <SkeletonRows rows={3} />
        </>
      )}

      {/* Filter toggle */}
      {!tokensQuery.isLoading && !tokensQuery.isError && (
        <div className="mb-4 flex gap-2">
          <Tooltip content={t("filter.thisTooltip")}>
            <Button
              variant={filterMode === "this" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setFilterMode("this")}
            >
              {t("filter.thisWorkspace")}
            </Button>
          </Tooltip>
          <Button
            variant={filterMode === "all" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setFilterMode("all")}
          >
            {t("filter.allTokens")}
          </Button>
        </div>
      )}

      {/* Content */}
      {tokensQuery.isSuccess && filteredTokens.length === 0 && (
        <EmptyState
          variant="panel"
          title="No personal access tokens yet"
          hint="Personal access tokens let you authenticate over HTTPS when cloning, pushing, and pulling repos without entering your password."
          action={
            <Button variant="primary" onClick={() => setCreating(true)}>
              + New token
            </Button>
          }
        />
      )}

      {tokensQuery.isSuccess && filteredTokens.length > 0 && (
        <TokensTable
          tokens={filteredTokens}
          currentWorkspaceId={currentWorkspaceId}
          onRevoke={(token) => {
            setRevokeError(null);
            setRevoking(token);
          }}
        />
      )}

      {/* Modals */}
      <NewTokenModal
        open={creating}
        currentWorkspaceId={currentWorkspaceId}
        currentWorkspaceName={workspace?.name ?? ""}
        currentWorkspaceSlug={slug}
        repos={reposQuery.data?.repos ?? []}
        onClose={() => setCreating(false)}
        onSubmit={handleCreate}
      />

      <RevokeConfirmDialog
        token={revoking}
        pending={revokeToken.isPending}
        error={revokeError}
        onConfirm={handleRevoke}
        onCancel={() => {
          setRevoking(null);
          setRevokeError(null);
        }}
      />
    </div>
  );
}
